import { useState, useEffect } from 'react'
import { X, Plus } from 'lucide-react'

const DISMISS_KEY = 'ios-install-dismissed'
const DISMISS_DAYS = 7

/** iOS Safari share icon (box with up arrow) */
function ShareIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  )
}

function getIsIOSSafari(): boolean {
  if (typeof window === 'undefined') return false
  const ua = navigator.userAgent
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  if (!isIOS) return false
  // Ensure it's Safari, not Chrome/Firefox/Edge on iOS
  return /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua)
}

function getIsStandalone(): boolean {
  if (typeof window === 'undefined') return false
  // iOS-specific standalone property
  if ((navigator as Navigator & { standalone?: boolean }).standalone === true) return true
  // Standard display-mode check
  if (window.matchMedia('(display-mode: standalone)').matches) return true
  return false
}

export function IOSInstallBanner() {
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (!getIsIOSSafari() || getIsStandalone()) return

    // Check localStorage dismiss
    const dismissedAt = localStorage.getItem(DISMISS_KEY)
    if (dismissedAt) {
      const elapsed = Date.now() - parseInt(dismissedAt, 10)
      if (elapsed < DISMISS_DAYS * 24 * 60 * 60 * 1000) return
    }

    // Small delay so it doesn't flash on load
    const timer = setTimeout(() => setShow(true), 2000)
    return () => clearTimeout(timer)
  }, [])

  const handleDismiss = () => {
    setShow(false)
    localStorage.setItem(DISMISS_KEY, String(Date.now()))
  }

  if (!show) return null

  return (
    <div className="fixed bottom-24 left-0 right-0 z-50 px-4 animate-[slideInFromBottom_0.3s_ease-out]">
      <div className="mx-auto max-w-sm bg-surface-primary rounded-2xl shadow-2xl border border-strong p-4">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <p className="font-semibold text-sm text-heading">
              홈 화면에 추가하세요
            </p>
            <p className="text-xs text-caption mt-1">
              앱처럼 빠르게 접근할 수 있습니다
            </p>
            <div className="mt-3 space-y-2 text-xs text-body">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 text-meta font-bold shrink-0">
                  1
                </span>
                <span className="flex items-center gap-1">
                  하단의 <ShareIcon className="w-4 h-4 text-blue-500 inline-block" /> 공유 버튼을 탭하세요
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 text-meta font-bold shrink-0">
                  2
                </span>
                <span className="flex items-center gap-1">
                  <Plus className="w-4 h-4 inline-block" /> 홈 화면에 추가를 선택하세요
                </span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            className="p-1 -mt-1 -mr-1 text-disabled hover:text-[var(--text-secondary)] transition-colors"
            aria-label="닫기"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
